// src/packages/care/prediction/cycleStats.ts
//
// Shared pure numeric + date helpers for the prediction models. Kept in one place so the
// Bayesian model and the calendar baseline (and future adapters) use the SAME date math,
// normal CDF, and cycle-gap extraction — no drift between models.

import { toLocalDateString, parseLocalDate } from "@src/packages/utils/date";
import { CYCLE_LENGTH } from "./knowledgeBase";
import type { LocalDate } from "./fertileWindow.types";

/** Whole-day difference `to − from` (both "YYYY-MM-DD"). */
export function daysBetween(from: LocalDate, to: LocalDate): number {
  const ms = parseLocalDate(to).getTime() - parseLocalDate(from).getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

/** Add a (possibly negative) whole number of days to a local date string. */
export function addDays(date: LocalDate, days: number): LocalDate {
  const d = parseLocalDate(date);
  d.setDate(d.getDate() + Math.round(days));
  return toLocalDateString(d);
}

/** Error function (Abramowitz & Stegun 7.1.26). */
export function erf(x: number): number {
  const t = 1 / (1 + 0.3275911 * Math.abs(x));
  const y =
    1 -
    ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) *
      t +
      0.254829592) *
      t *
      Math.exp(-x * x);
  return x >= 0 ? y : -y;
}

/** Standard-normal CDF. */
export function normalCdf(z: number): number {
  return 0.5 * (1 + erf(z / Math.SQRT2));
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function mean(values: number[]): number {
  return values.reduce((acc, v) => acc + v, 0) / values.length;
}

/**
 * Observed cycle lengths (start→start gaps) from sorted period-start dates, keeping only
 * plausible gaps. Gaps outside the plausible range are dropped as logging errors / skipped
 * cycles so they don't bias the estimate. [knowledgeBase.CYCLE_LENGTH.plausibleRangeDays]
 */
export function getObservedCycleLengths(starts: LocalDate[]): number[] {
  const { min, max } = CYCLE_LENGTH.plausibleRangeDays;
  const lengths: number[] = [];
  for (let i = 1; i < starts.length; i++) {
    const gap = daysBetween(starts[i - 1]!, starts[i]!);
    if (gap >= min && gap <= max) {
      lengths.push(gap);
    }
  }
  return lengths;
}
